import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, makeStyles } from '@material-ui/core'
import ItemStatusEditor from '../../../../ItemStatusEditor'
import { useAssignedAttachmentsByClient } from '../../../../../../../api/documentVault'
import { useAppContext } from '../../../../../../../redux/slices/appContext'
import { useAddComment, useCommentThreadDetails, useCreateCommentThread } from '../../../../../../../api/comments'
import StatusDisplayBoardLayout from '../StatusDisplayBoardLayout'
import SydButton from '../../../../../../commonDesign/Button'
import Attachment from './Attachment'
import CommentList from './CommentList'

const useStyles = makeStyles(() => ({
  '@keyframes slideInFromRight': {
    '0%': {
      opacity: 0,
      transform: 'translateX(100%)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)'
    }
  },
  '@keyframes slideOutToRight': {
    '0%': {
      opacity: 1,
      transform: 'translateX(0)'
    },
    '100%': {
      opacity: 0,
      transform: 'translateX(100%)'
    }
  },
  detailPanel: {
    position: 'sticky',
    top: 'var(--dim-4)',
    alignSelf: 'flex-start',
    flex: '0 0 400px',
    maxHeight: ({ dynamicMaxHeight }) => dynamicMaxHeight || 'calc(100vh - var(--dim-4) * 4)',
    minHeight: '600px',
    borderRadius: 'var(--border-radius-lg)',
    background: 'var(--color-neutral-100)',
    border: 'var(--border-neutral-thin)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: ({ isClosing }) => isClosing
      ? '$slideOutToRight 0.25s ease-in forwards'
      : '$slideInFromRight 0.3s ease-out'
  },
  detailPanelTitle: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: 'var(--border-neutral-thin)',
    padding: 'var(--dim-2) var(--dim-4)',
    color: 'var(--color-neutral-800)'
  },
  detailPanelContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-4)',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 'var(--dim-4)'
  },
  detailList: {
    display: 'flex',
    flexDirection: 'column',
    '& .__detail-item': {
      display: 'flex',
      gap: 'var(--dim-4)',
      padding: 'var(--dim-2) 0',
      alignItems: 'center'
    },
    '& .__detail-item:not(:last-child)': {
      borderBottom: 'var(--border-neutral-thin)'
    },
    '& .__detail-item-header': {
      minWidth: '100px',
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-neutral-800)',
      fontWeight: 'var(--font-weight-light)',
      margin: 0
    },
    '& .__detail-item-content': {
      flex: 1
    }
  },
  commentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-3)',
    flex: 1,
    minHeight: 0,
    '& .__comments-header': {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-neutral-800)',
      fontWeight: 'var(--font-weight-light)',
      margin: 0
    }
  }
}))

function StatusReportDetailsPanel ({
  item,
  statuses,
  onClose,
  onUpdate
}) {
  const panelRef = useRef(null)
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState(null)
  const [threadKey, setThreadKey] = useState(item?.threadKey || null)

  useEffect(() => {
    setThreadKey(item?.threadKey || null)
  }, [item?.threadKey])

  // Calculate dynamic max height based on panel's position
  useEffect(() => {
    let rafId = null

    const calculateMaxHeight = () => {
      if (panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect()
        const topOffset = rect.top
        const bottomMargin = 16 // Additional margin from bottom
        const calculatedMaxHeight = `${window.innerHeight - topOffset - bottomMargin}px`
        setDynamicMaxHeight(calculatedMaxHeight)
      }
    }

    const handleScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(calculateMaxHeight)
    }

    const handleResize = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(calculateMaxHeight)
    }

    calculateMaxHeight()
    window.addEventListener('scroll', handleScroll, true) // Use capture phase
    window.addEventListener('resize', handleResize)
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const classes = useStyles({ dynamicMaxHeight })
  const { clientId } = useAppContext()

  const { data: attachments, isLoading: attachmentsLoading, refetch: refetchAttachments } = useAssignedAttachmentsByClient(278, item.statusTemplateItemId, clientId)

  const { data: commentData, isLoading: commentsLoading, refetch: refetchComments } = useCommentThreadDetails(
    threadKey,
    278,
    item.statusTemplateItemId,
    clientId
  )

  const { mutateAsync: createThreadAsync, isLoading: createThreadIsLoading } = useCreateCommentThread()
  const { mutateAsync: addCommentAsync, isLoading: addCommentIsLoading } = useAddComment()

  const isLoading = useMemo(() => attachmentsLoading || (!!item.threadKey && commentsLoading), [attachmentsLoading, commentsLoading, item.threadKey])

  // Transform comment data to match our component's expected format
  const comments = useMemo(() => {
    if (!commentData?.comments) return []

    return commentData.comments.map(comment => {
      const author = commentData.authors?.find(a => a.userId === comment.author)
      return {
        commentId: comment.commentKey,
        text: comment.body,
        createdDate: comment.publishDate,
        userId: comment.author,
        userName: author?.authorName || 'Unknown User',
        userAvatar: author?.avatarUrl || null,
        avatarType: author?.avatarType || 'media'
      }
    })
  }, [commentData])

  const handleAddComment = useCallback(async (text) => {
    if (!text.trim()) return

    try {
      if (item.threadKey) {
        await addCommentAsync({
          threadKey: item.threadKey,
          body: text
        })
      } else {
        const threadData = await createThreadAsync({
          clientId,
          levelTypeId: 278,
          levelId: item.statusTemplateItemId,
          body: text
        })
        setThreadKey(threadData.threadKey)
        onUpdate?.()
      }

      await refetchComments()
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }, [item.threadKey, item.statusTemplateItemId, refetchComments, addCommentAsync, createThreadAsync, clientId, onUpdate])

  return (
    <div ref={panelRef} className={classes.detailPanel}>
      {item?.parent && (
        <div className={classes.detailPanelTitle}>
          {item?.parent?.displayName}

          <SydButton
            size='xs'
            variant='secondary'
            onClick={onClose}
          >
            Close
          </SydButton>
        </div>
      )}
      <div className={classes.detailPanelContent}>
        <div className='flex flex-col gap-2'>
          <StatusDisplayBoardLayout statusInfo={item.statusInfo} size={30} useIcon hasBackground />
          <p className='font-size-lg m-0'>{item.displayName}</p>
          {!!item.description?.length && (
            <p className='font-size-xs font-light m-0'>{item.description}</p>
          )}
        </div>
        {isLoading ? (
          <div className='flex-1 flex items-center justify-center'>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className={classes.detailList}>
              <div className='__detail-item'>
                <p className='__detail-item-header'>Status</p>
                <div className='__detail-item-content'>
                  <ItemStatusEditor
                    item={item}
                    statusInfo={item.statusInfo}
                    statuses={statuses}
                    isFetching={false}
                    style={{ padding: 0 }}
                  />
                </div>
              </div>
              {!!attachments?.attachments?.length && (
                <div className='__detail-item'>
                  <p className='__detail-item-header'>Attachments</p>
                  <div className='__detail-item-content'>
                    {attachments.attachments?.map((attachment) => (
                      <Attachment key={attachment.attachmentId} attachment={attachment} onRemoveAttachment={refetchAttachments} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={classes.commentsContainer}>
              <p className='__comments-header'>Comments</p>
              <CommentList
                comments={comments}
                onAddComment={handleAddComment}
                processing={createThreadIsLoading || addCommentIsLoading}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

StatusReportDetailsPanel.propTypes = {
  item: PropTypes.shape({
    statusTemplateItemId: PropTypes.number,
    displayName: PropTypes.string,
    description: PropTypes.string,
    statusInfo: PropTypes.shape({
      color: PropTypes.string,
      displayName: PropTypes.string,
      icon: PropTypes.string
    }),
    threadKey: PropTypes.string,
    parent: PropTypes.shape({
      displayName: PropTypes.string
    })
  }),
  statuses: PropTypes.arrayOf(PropTypes.shape({
    statusId: PropTypes.number,
    codeName: PropTypes.string,
    displayName: PropTypes.string,
    color: PropTypes.string,
    icon: PropTypes.string
  })),
  onClose: PropTypes.func,
  onUpdate: PropTypes.func
}

export default StatusReportDetailsPanel
